import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  Plus, 
  GripVertical, 
  Trash2, 
  Settings, 
  Download, 
  Eye,
  Copy,
  RotateCcw,
  Edit,
  Library,
  Sparkles
} from 'lucide-react';
import { toast } from "sonner";
import { ConditionalLogicDialog } from './ConditionalLogicDialog';
import { RSVPFormPreview } from './RSVPFormPreview';
import { QuestionTemplateLibrary } from './QuestionTemplateLibrary';
import { useIsMobile } from '@/hooks/use-mobile';

export interface ConditionalLogic {
  id: string;
  triggerQuestionId: string;
  triggerValue: string;
  action: 'show' | 'hide' | 'required' | 'skip';
  targetQuestionIds: string[];
}

export interface CustomRSVPQuestion {
  id: string;
  text: string;
  type: 'text' | 'textarea' | 'select' | 'multiselect' | 'radio' | 'checkbox' | 'date' | 'time' | 'number' | 'email' | 'phone';
  options?: string[];
  required: boolean;
  category: string;
  placeholder?: string;
  helpText?: string;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
  };
  conditionalLogic?: ConditionalLogic[];
}

interface CustomRSVPBuilderProps {
  questions: CustomRSVPQuestion[];
  onQuestionsChange: (questions: CustomRSVPQuestion[]) => void;
  eventId?: string;
}

interface SortableQuestionProps {
  question: CustomRSVPQuestion;
  questions: CustomRSVPQuestion[];
  onUpdate: (id: string, updates: Partial<CustomRSVPQuestion>) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
}

const QUESTION_TYPES = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'select', label: 'Dropdown' },
  { value: 'multiselect', label: 'Multiple Choice' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'checkbox', label: 'Checkboxes' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
  { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
];

const QUESTION_CATEGORIES = [
  'General',
  'Contact',
  'Preferences',
  'Dietary',
  'Accessibility',
  'Medical',
  'Emergency',
  'Custom'
];

function SortableQuestion({ question, questions, onUpdate, onDelete, onDuplicate }: SortableQuestionProps) {
  const isMobile = useIsMobile();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState(question);
  const [showConditionalLogic, setShowConditionalLogic] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleSave = () => {
    onUpdate(question.id, editForm);
    setIsEditing(false);
    toast.success('Question updated successfully');
  };

  const handleCancel = () => {
    setEditForm(question);
    setIsEditing(false);
  };

  const handleConditionalLogicSave = (logic: ConditionalLogic[]) => {
    const updatedQuestion = { ...editForm, conditionalLogic: logic };
    setEditForm(updatedQuestion);
    onUpdate(question.id, updatedQuestion);
  };

  const addOption = () => {
    setEditForm({
      ...editForm,
      options: [...(editForm.options || []), '']
    });
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...(editForm.options || [])];
    newOptions[index] = value;
    setEditForm({
      ...editForm,
      options: newOptions
    });
  };

  const removeOption = (index: number) => {
    setEditForm({
      ...editForm,
      options: editForm.options?.filter((_, i) => i !== index)
    });
  };

  const needsOptions = ['select', 'multiselect', 'radio', 'checkbox'].includes(editForm.type);

  return (
    <>
      <Card 
        ref={setNodeRef} 
        style={style} 
        className={`mb-3 md:mb-4 group transition-all duration-200 hover:shadow-md ${
          isEditing ? 'ring-2 ring-primary/20 shadow-lg' : ''
        }`}
      >
        <CardHeader className="pb-2 md:pb-3 bg-gradient-to-r from-card to-muted/20">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
              <div 
                {...attributes} 
                {...listeners} 
                className="cursor-grab hover:cursor-grabbing touch-none opacity-50 group-hover:opacity-100 transition-opacity"
              >
                <GripVertical className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <CardTitle className="text-sm md:text-base font-semibold truncate">{question.text}</CardTitle>
                <div className="flex items-center gap-1 md:gap-2 mt-1.5 flex-wrap">
                  <Badge variant="secondary" className="text-xs font-medium">
                    {isMobile ? question.type : QUESTION_TYPES.find(t => t.value === question.type)?.label}
                  </Badge>
                  {!isMobile && <Badge variant="outline" className="text-xs font-normal">{question.category}</Badge>}
                  {question.required && (
                    <Badge className="text-xs bg-gradient-to-r from-destructive to-destructive/80">
                      Required
                    </Badge>
                  )}
                  {question.conditionalLogic && question.conditionalLogic.length > 0 && (
                    <Badge className="text-xs bg-gradient-to-r from-primary to-primary-light">
                      {question.conditionalLogic.length} {isMobile ? 'Rule' : `Rule${question.conditionalLogic.length > 1 ? 's' : ''}`}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-0.5 md:gap-1">
              <Button
                variant="ghost"
                size={isMobile ? "icon" : "sm"}
                onClick={() => onDuplicate(question.id)}
                className="h-8 w-8 md:h-9 md:w-9 hover:bg-primary/10 hover:text-primary transition-colors"
              >
                <Copy className="h-3 w-3 md:h-4 md:w-4" />
              </Button>
              <Button
                variant={isEditing ? "default" : "ghost"}
                size={isMobile ? "icon" : "sm"}
                onClick={() => setIsEditing(!isEditing)}
                className="h-8 w-8 md:h-9 md:w-9 transition-all"
              >
                <Edit className="h-3 w-3 md:h-4 md:w-4" />
              </Button>
              <Button
                variant="ghost"
                size={isMobile ? "icon" : "sm"}
                onClick={() => onDelete(question.id)}
                className="h-8 w-8 md:h-9 md:w-9 hover:bg-destructive/10 hover:text-destructive transition-colors"
              >
                <Trash2 className="h-3 w-3 md:h-4 md:w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        {isEditing && (
          <CardContent className="space-y-5 pt-4 border-t bg-muted/30">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Question Text</Label>
                <Input
                  value={editForm.text}
                  onChange={(e) => setEditForm({ ...editForm, text: e.target.value })}
                  placeholder="Enter your question"
                  className="bg-background"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Question Type</Label>
                <Select
                  value={editForm.type}
                  onValueChange={(value) => setEditForm({ ...editForm, type: value as any })}
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUESTION_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Category</Label>
                <Select
                  value={editForm.category}
                  onValueChange={(value) => setEditForm({ ...editForm, category: value })}
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUESTION_CATEGORIES.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Placeholder Text</Label>
                <Input
                  value={editForm.placeholder || ''}
                  onChange={(e) => setEditForm({ ...editForm, placeholder: e.target.value })}
                  placeholder="Optional placeholder"
                  className="bg-background"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Help Text</Label>
              <Textarea
                value={editForm.helpText || ''}
                onChange={(e) => setEditForm({ ...editForm, helpText: e.target.value })}
                placeholder="Optional help text for guests"
                rows={2}
                className="bg-background"
              />
            </div>

            {needsOptions && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Options</Label>
                <div className="space-y-2 p-4 rounded-lg bg-background border">
                  {editForm.options?.map((option, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={option}
                        onChange={(e) => updateOption(index, e.target.value)}
                        placeholder={`Option ${index + 1}`}
                        className="flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeOption(index)}
                        className="hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button 
                    variant="outline" 
                    onClick={addOption} 
                    className="w-full border-dashed hover:bg-primary/5 hover:border-primary"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Option
                  </Button>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 p-4 rounded-lg bg-background border">
              <Switch
                checked={editForm.required}
                onCheckedChange={(checked) => setEditForm({ ...editForm, required: checked })}
              />
              <Label className="cursor-pointer font-medium">Mark as required field</Label>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setShowConditionalLogic(true)}
                className="hover:bg-primary/5 hover:border-primary"
              >
                <Settings className="h-4 w-4 mr-2" />
                Conditional Logic
                {question.conditionalLogic && question.conditionalLogic.length > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {question.conditionalLogic.length}
                  </Badge>
                )}
              </Button>
              <div className="flex gap-2 w-full sm:w-auto">
                <Button variant="outline" onClick={handleCancel} className="flex-1 sm:flex-none">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSave} className="flex-1 sm:flex-none bg-gradient-to-r from-primary to-primary-light">
                  Save Changes
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      <ConditionalLogicDialog
        open={showConditionalLogic}
        onOpenChange={setShowConditionalLogic}
        question={editForm}
        allQuestions={questions}
        onSave={handleConditionalLogicSave}
      />
    </>
  );
}

export function CustomRSVPBuilder({ questions, onQuestionsChange, eventId }: CustomRSVPBuilderProps) {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('builder');
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = questions.findIndex(q => q.id === active.id);
      const newIndex = questions.findIndex(q => q.id === over?.id);
      
      onQuestionsChange(arrayMove(questions, oldIndex, newIndex));
    }
  };

  const addQuestion = () => {
    const newQuestion: CustomRSVPQuestion = {
      id: `question_${Date.now()}`,
      text: 'New Question',
      type: 'text',
      required: false,
      category: 'General',
    };
    
    onQuestionsChange([...questions, newQuestion]);
  };

  const updateQuestion = (id: string, updates: Partial<CustomRSVPQuestion>) => {
    onQuestionsChange(
      questions.map(q => q.id === id ? { ...q, ...updates } : q)
    );
  };

  const deleteQuestion = (id: string) => {
    onQuestionsChange(questions.filter(q => q.id !== id));
  };

  const duplicateQuestion = (id: string) => {
    const questionToDuplicate = questions.find(q => q.id === id);
    if (questionToDuplicate) {
      const duplicated: CustomRSVPQuestion = {
        ...questionToDuplicate,
        id: `question_${Date.now()}`,
        text: `${questionToDuplicate.text} (Copy)`,
      };
      onQuestionsChange([...questions, duplicated]);
      toast.success('Question duplicated successfully');
    }
  };

  const addQuestionsFromTemplate = (templateQuestions: CustomRSVPQuestion[]) => {
    onQuestionsChange([...questions, ...templateQuestions]);
  };

  const exportData = () => {
    const exportData = {
      questions,
      eventId,
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rsvp-form-${eventId || 'template'}-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('RSVP form exported successfully');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between pb-4 border-b">
        <div>
          <h3 className="text-2xl font-bold bg-gradient-to-r from-primary to-primary-light bg-clip-text text-transparent flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            Custom RSVP Builder
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {isMobile ? 'Create dynamic RSVP forms' : 'Build intelligent RSVP forms with drag-and-drop questions and conditional logic'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <QuestionTemplateLibrary onAddQuestions={addQuestionsFromTemplate} />
          {!isMobile && (
            <Button variant="outline" onClick={exportData} size="default" className="hover:bg-primary/5 hover:border-primary">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          )}
          <Button 
            onClick={addQuestion} 
            size={isMobile ? "default" : "default"} 
            className="flex-1 sm:flex-none bg-gradient-to-r from-primary to-primary-light hover:shadow-lg transition-all"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Question
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 h-auto gap-1">
          <TabsTrigger value="builder" className="text-xs sm:text-sm py-2 px-2 sm:px-4">
            {isMobile ? 'Build' : 'Builder'}
          </TabsTrigger>
          <TabsTrigger value="preview" className="text-xs sm:text-sm py-2 px-2 sm:px-4">
            Preview
          </TabsTrigger>
          <TabsTrigger value="logic" className="text-xs sm:text-sm py-2 px-2 sm:px-4">
            Logic
          </TabsTrigger>
          <TabsTrigger value="analytics" className="text-xs sm:text-sm py-2 px-2 sm:px-4">
            Stats
          </TabsTrigger>
        </TabsList>

        <TabsContent value="builder" className="space-y-4">
          {questions.length === 0 ? (
            <Card className="border-dashed border-2 bg-gradient-to-br from-muted/30 to-muted/10">
              <CardContent className="py-16 text-center">
                <div className="max-w-md mx-auto space-y-6">
                  <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary/20 to-primary-light/20 flex items-center justify-center">
                    <Library className="h-10 w-10 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-semibold">Build Your Perfect RSVP Form</h3>
                    <p className="text-muted-foreground text-sm">
                      Start with professional templates or create custom questions from scratch
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                    <QuestionTemplateLibrary onAddQuestions={addQuestionsFromTemplate} />
                    <Button onClick={addQuestion} variant="outline" size="lg" className="gap-2">
                      <Plus className="h-5 w-5" />
                      Create Custom Question
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={questions.map(q => q.id)} strategy={verticalListSortingStrategy}>
                {questions.map((question) => (
                  <SortableQuestion
                    key={question.id}
                    question={question}
                    questions={questions}
                    onUpdate={updateQuestion}
                    onDelete={deleteQuestion}
                    onDuplicate={duplicateQuestion}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </TabsContent>

        <TabsContent value="preview" className="space-y-4">
          <RSVPFormPreview 
            questions={questions} 
            onSubmit={() => {}} 
            isPreview={true} 
          />
        </TabsContent>

        <TabsContent value="logic" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Conditional Logic Rules</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Conditional logic rules configured across all questions. Edit individual questions to add or modify rules.
              </p>
              
              {questions.some(q => q.conditionalLogic && q.conditionalLogic.length > 0) ? (
                <div className="space-y-4">
                  {questions.map(question => {
                    if (!question.conditionalLogic || question.conditionalLogic.length === 0) return null;
                    
                    return (
                      <div key={question.id} className="border rounded-lg p-4">
                        <h4 className="font-medium mb-2">{question.text}</h4>
                        <div className="space-y-2">
                          {question.conditionalLogic.map((rule, index) => {
                            const triggerQuestion = questions.find(q => q.id === rule.triggerQuestionId);
                            const targetQuestions = questions.filter(q => rule.targetQuestionIds.includes(q.id));
                            
                            return (
                              <div key={rule.id} className="text-sm bg-muted p-2 rounded">
                                <span className="font-medium">Rule {index + 1}:</span> When "
                                <span className="font-medium">{triggerQuestion?.text || 'Unknown'}</span>" is "
                                <span className="font-medium">{rule.triggerValue}</span>", then {rule.action} "
                                <span className="font-medium">
                                  {targetQuestions.map(q => q.text).join(', ') || 'Unknown questions'}
                                </span>"
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No conditional logic rules configured yet. Edit questions to add conditional logic.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Response Analytics</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Analytics and export capabilities will be available once responses are collected.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}